<script setup lang="ts">
import { SearchOutlined, CloseCircleFilled } from "@ant-design/icons-vue";
import { message, type TreeProps } from "ant-design-vue";
import { computed, ref, watch } from "vue";
import { type AdminListItem } from "@/services/oaOffice/role";
import { buildSelectedUserList, fetchSelectedAdminList } from "./shared";
import type { SelectedUser } from "./types";

interface MemberModalProps {
	multiple?: boolean;
	maxCount?: number;
}

const props = withDefaults(defineProps<MemberModalProps>(), {
	multiple: false,
	maxCount: 0,
});

const open = defineModel<boolean>("open", { default: false });
const selectedUsers = defineModel<SelectedUser[]>({ default: () => [] });
const loading = ref(false);

const adminList = ref<TreeProps["treeData"]>([]);
const userList = ref<{ id: number; realname: string }[]>([]);

const hasDepartmentMembers = (item: AdminListItem): boolean => {
	if (item.admin_list.length > 0) {
		return true;
	}

	if (!item.children?.length) {
		return false;
	}

	return item.children.some((child) => hasDepartmentMembers(child));
};

const normalizeTreeData = (list: AdminListItem[]) => {
	return list.map((item) => {
		const memberList = item.admin_list.map((admin) => ({
			title: admin.realname,
			key: `${item.dept_id}-${admin.id}`,
			pid: item.dept_id,
		}));

		const children = item.children ? normalizeTreeData(item.children) : [];

		return {
			title: item.dept_name,
			key: item.dept_id,
			pid: item.pid,
			isEmptyDept: !hasDepartmentMembers(item),
			children: [...children, ...memberList],
		};
	});
};

const getAdminList = async () => {
	try {
		loading.value = true;
		const adminItems = await fetchSelectedAdminList();

		adminList.value = normalizeTreeData(adminItems);
		userList.value = buildSelectedUserList(adminItems).filter(
			(user, index, self) => index === self.findIndex((u) => u.id === user.id)
		);
	} finally {
		loading.value = false;
	}
};

const searchValue = ref("");
const checkedKeys = ref<Key[]>([]);
const expandedKeys = ref<Key[]>([]);

const isMemberKey = (key: Key) => String(key).includes("-");

const normalizedMaxCount = computed(() => {
	const count = Number(props.maxCount ?? 0);

	if (Number.isNaN(count) || count <= 0) {
		return 0;
	}

	return Math.floor(count);
});

interface TreeCheckNode {
	key: Key;
	children?: TreeCheckNode[];
}

interface SearchTreeNode {
	key?: Key;
	title?: string;
	children?: SearchTreeNode[];
}

const hasMemberDescendant = (node: TreeCheckNode) => {
	if (isMemberKey(node.key)) {
		return true;
	}

	if (!node.children?.length) {
		return false;
	}

	return node.children.some((child) => hasMemberDescendant(child));
};

const handleCheck: TreeProps["onCheck"] = (value, info) => {
	if (info.checked && !isMemberKey(info.node.key) && !hasMemberDescendant(info.node)) {
		message.warning("当前部门下暂无可选成员");
	}

	const nextCheckedKeys = Array.isArray(value) ? value : value.checked;
	const memberKeys = nextCheckedKeys.filter(isMemberKey);
	let finalCheckedKeys = memberKeys;

	if (!props.multiple) {
		if (info.checked && isMemberKey(info.node.key as Key)) {
			finalCheckedKeys = [info.node.key as Key];
		} else {
			finalCheckedKeys = memberKeys.slice(0, 1);
		}
	} else {
		const maxCount = normalizedMaxCount.value;

		if (maxCount > 0 && memberKeys.length > maxCount) {
			message.warning(`最多可选择 ${maxCount} 个成员`);

			const nodeKey = String(info.node.key);
			const previousKeys = checkedKeys.value.map((key) => String(key));

			if (info.checked && isMemberKey(info.node.key as Key) && !previousKeys.includes(nodeKey)) {
				finalCheckedKeys = [...checkedKeys.value];
			} else {
				finalCheckedKeys = memberKeys.slice(0, maxCount);
			}
		}
	}

	checkedKeys.value = finalCheckedKeys;
};

const checkedUserList = computed(() => {
	const checkedUserIds = new Set(
		checkedKeys.value
			.map((key) => String(key))
			.filter((key) => key.includes("-"))
			.map((key) => Number(key.split("-")[1]))
	);

	return userList.value.filter((user) => checkedUserIds.has(user.id));
});

const isLimitReached = computed(
	() =>
		props.multiple &&
		normalizedMaxCount.value > 0 &&
		checkedUserList.value.length >= normalizedMaxCount.value
);

const limitedTreeData = computed<TreeProps["treeData"]>(() => {
	if (!adminList.value) {
		return [];
	}

	const checkedKeySet = new Set(checkedKeys.value.map((key) => String(key)));

	const mapNodes = (nodes: TreeProps["treeData"]) => {
		if (!nodes) {
			return [];
		}

		return nodes.map((node) => {
			const isMember = isMemberKey(node.key as Key);
			const isChecked = checkedKeySet.has(String(node.key));

			return {
				...node,
				disableCheckbox: isLimitReached.value && isMember && !isChecked,
				children: node.children ? mapNodes(node.children as TreeProps["treeData"]) : node.children,
			};
		});
	};

	return mapNodes(adminList.value);
});

const initCheckedKeysFromUsers = () => {
	if (!selectedUsers.value?.length) {
		return;
	}

	const userIds = new Set(selectedUsers.value.map((u) => u.id));
	const keys: Key[] = [];

	const traverse = (nodes: TreeProps["treeData"]) => {
		if (!nodes) {
			return;
		}

		nodes.forEach((node) => {
			if (node.key !== null && node.key !== undefined && isMemberKey(node.key as Key)) {
				const userId = Number(String(node.key).split("-")[1]);

				if (userIds.has(userId)) {
					keys.push(node.key as Key);
				}
			}

			if (node.children) {
				traverse(node.children as TreeProps["treeData"]);
			}
		});
	};

	traverse(adminList.value);
	checkedKeys.value = keys;
};

const handleDelete = (id: number) => {
	const value = checkedKeys.value.filter((key) => key.toString().includes(`-${id}`) === false);

	checkedKeys.value = value;
};

const collectExpandedKeysBySearch = (nodes: SearchTreeNode[], keyword: string) => {
	const expandedKeySet = new Set<Key>();

	const traverse = (currentNodes: SearchTreeNode[], parentKeys: Key[]) => {
		currentNodes.forEach((node) => {
			const currentKey = node.key;
			const currentTitle = String(node.title ?? "");
			const nextParentKeys = currentKey !== undefined ? [...parentKeys, currentKey] : parentKeys;
			const children = Array.isArray(node.children) ? node.children : [];
			const isMatched = currentTitle.includes(keyword);

			if (isMatched) {
				parentKeys.forEach((key) => {
					expandedKeySet.add(key);
				});

				if (currentKey !== undefined && children.length > 0) {
					expandedKeySet.add(currentKey);
				}
			}

			if (children.length > 0) {
				traverse(children, nextParentKeys);
			}
		});
	};

	traverse(nodes, []);

	return [...expandedKeySet];
};

watch(searchValue, (value) => {
	if (!value) {
		expandedKeys.value = [];

		return;
	}

	expandedKeys.value = collectExpandedKeysBySearch(
		(adminList.value as SearchTreeNode[] | undefined) ?? [],
		value
	);
});

const handleCancel = () => {
	open.value = false;
	checkedKeys.value = [];
};

const handleConfirm = async () => {
	try {
		selectedUsers.value = [...checkedUserList.value];
		open.value = false;
	} catch (_error) {
	} finally {
		loading.value = false;
	}
};

watch(
	() => open.value,
	async (value) => {
		if (value) {
			checkedKeys.value = [];
			await getAdminList();
			initCheckedKeysFromUsers();
		}
	}
);
</script>

<template>
	<custom-modal
		v-model:open="open"
		title="添加成员"
		:confirm-loading="loading"
		:width="900"
		:footer="null"
		:body-style="{ padding: 0 }"
		@ok="handleConfirm"
	>
		<a-row class="h-[600px] w-full">
			<a-col
				class="full border-wireframe py-mm px-ml gap-ml relative !flex flex-col border-r border-solid"
				:span="10"
			>
				<a-input v-model:value="searchValue" placeholder="请输入内容">
					<template #suffix>
						<SearchOutlined class="cursor-pointer" />
					</template>
				</a-input>

				<div class="min-h-0 flex-1 overflow-auto">
					<a-tree
						v-model:expanded-keys="expandedKeys"
						:checked-keys="checkedKeys"
						class="user-select"
						block-node
						checkable
						:tree-data="limitedTreeData"
						:selectable="false"
						@check="handleCheck"
					>
						<template #title="{ title, isEmptyDept }">
							<div class="pr-ls gap-sm inline-flex max-w-full min-w-0 items-center">
								<div class="line-clamp-1 min-w-0 shrink">
									<span v-if="title.includes(searchValue)">
										{{ title.substring(0, title.indexOf(searchValue)) }}
										<span class="text-primary">{{ searchValue }}</span>
										{{ title.substring(title.indexOf(searchValue) + searchValue.length) }}
									</span>
									<span v-else>{{ title }}</span>
								</div>
								<span
									v-if="isEmptyDept"
									class="text-text-desc bg-white-ash p-ty shrink-0 rounded text-sm leading-none"
								>
									暂无成员
								</span>
							</div>
						</template>
					</a-tree>
				</div>

				<div v-if="loading" class="full flex-row-center absolute top-0 left-0 bg-white/80">
					<a-spin></a-spin>
				</div>
			</a-col>
			<a-col class="py-mm px-ml gap-mm !flex flex-col" :span="14">
				<div class="gap-sm flex items-center">
					<span>
						已选择
						<span class="text-text-desc">
							（{{ checkedUserList.length }}
							<span v-if="props.multiple && normalizedMaxCount > 0">/{{ normalizedMaxCount }}</span>
							）
						</span>
					</span>
				</div>

				<div class="gap-smm flex min-h-0 flex-1 flex-wrap content-start overflow-auto">
					<div
						v-for="(item, index) in checkedUserList"
						:key="index"
						class="p-ts bg-white-ash gap-mms border-muted flex cursor-pointer items-center rounded border border-solid"
					>
						<span class="text-sm text-black/85">{{ item.realname }}</span>

						<CloseCircleFilled
							class="!text-text-desc cursor-pointer"
							@click="handleDelete(item.id)"
						/>
					</div>
				</div>

				<div class="gap-ms pb-ms pr-ms flex items-center justify-end">
					<a-button class="w-5xh" @click="handleCancel">取消</a-button>
					<a-button class="w-5xh" type="primary" :loading="loading" @click="handleConfirm">
						确定
					</a-button>
				</div>
			</a-col>
		</a-row>
	</custom-modal>
</template>

<style lang="scss" scoped>
:deep(.user-select) {
	.ant-tree-treenode {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 0;
		margin-bottom: 6px;

		&:hover {
			border-radius: 2px;
			background-color: color-mix(in srgb, var(--color-driver) 20%, transparent);
		}
	}

	.ant-tree-switcher {
		line-height: 0;
		width: 14px;
		align-self: center;
	}

	.ant-tree-node-content-wrapper {
		min-height: 36px;
		line-height: 36px;

		&:hover {
			background-color: transparent;
		}
	}

	.ant-tree-treenode-selected {
		border-radius: 2px;
		background-color: var(--color-secondary) !important;
	}

	.ant-tree-node-selected {
		background-color: transparent;
	}

	.ant-tree-checkbox {
		margin: 0;
		position: absolute;
		right: 8px;
		z-index: 99;
	}
}
</style>
